'use client';

import { useEffect } from 'react';
import { isChunkLoadError } from '@/lib/chunk-load-error';

const RELOAD_FLAG = 'flo-chunk-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    console.error('GlobalError boundary caught:', error);
    if (!chunkError || typeof window === 'undefined') return;
    try {
      const last = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? '0');
      if (Date.now() - last > RELOAD_COOLDOWN_MS) {
        window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        window.location.reload();
      }
    } catch {
      // sessionStorage may be unavailable; user can tap Refresh.
    }
  }, [chunkError, error]);

  return (
    <html lang="en">
      <body>
        <div
          className="flex flex-col items-center justify-center text-center min-h-screen p-8 gap-4"
          role="alert"
          aria-live={chunkError ? 'polite' : 'assertive'}
        >
          <h1 className="text-2xl text-[#1a1a2e] m-0">
            {chunkError ? 'EduCity was just updated' : 'Something went wrong'}
          </h1>
          <p className="text-base text-[#6b7280] max-w-sm m-0">
            {chunkError
              ? 'Refreshing to load the latest version…'
              : 'A critical error occurred. Please try again.'}
          </p>
          <button
            className="py-3 px-6 text-base rounded-lg border-0 bg-[#7c3aed] text-white cursor-pointer min-h-[2.75rem] hover:bg-[#6d28d9]"
            onClick={chunkError ? () => window.location.reload() : reset}
          >
            {chunkError ? 'Refresh now' : 'Try again'}
          </button>
        </div>
      </body>
    </html>
  );
}
